export interface RoleScopeInfo {
  role_id?: string
  role_name?: string
  role_level?: string
  company_id?: string
  company_code?: string
  company_name?: string
  company?: {
    id?: string
    company_id?: string
    code?: string
    company_code?: string
    name?: string
    company_name?: string
  }
  [key: string]: unknown
}

export interface CompanyScopeOption {
  company_id: string
  company_name: string
  company_code?: string
  company_type?: string
}

export interface CompanyAutocompleteScopeResult {
  shouldShowAutocomplete: boolean
  hasGlobalRole: boolean
  mode: 'global' | 'company' | 'hidden'
  companies: CompanyScopeOption[]
  roleCompanyIds: string[]
  defaultCompanyId: string | null
  defaultCompany: CompanyScopeOption | null
}

export interface ResolveCompanyAutocompleteScopeInput {
  roles?: RoleScopeInfo[]
  availableCompanies?: CompanyScopeOption[]
  selectedCompany?: Partial<CompanyScopeOption> | null
  includeSelectedCompanyFallback?: boolean
}

const AUTH_PERSIST_KEY = 'persist:v100-xbox-auth'
const SELECTED_COMPANY_KEY = 'selected_company'
const REMEMBER_COMPANY_KEY = 'remember_company'

const normalizeString = (value: unknown): string => {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim()
}

const roleLevelIsGlobal = (role: RoleScopeInfo): boolean => {
  return normalizeString(role?.role_level).toLowerCase() === 'global'
}

const getCompanyIdFromCompanyOption = (company?: Partial<CompanyScopeOption> | null): string => {
  return normalizeString(company?.company_id)
}

const toCompanyOption = (company?: Partial<CompanyScopeOption> | null): CompanyScopeOption | null => {
  if (!company) {
    return null
  }

  const companyId = getCompanyIdFromCompanyOption(company)
  if (!companyId) {
    return null
  }

  return {
    company_id: companyId,
    company_name: normalizeString(company.company_name) || companyId,
    company_code: normalizeString(company.company_code),
    company_type: normalizeString(company.company_type) || 'company'
  }
}

const dedupeCompanies = (companies: CompanyScopeOption[]): CompanyScopeOption[] => {
  const map = new Map<string, CompanyScopeOption>()

  companies.forEach((company) => {
    const companyId = getCompanyIdFromCompanyOption(company)
    if (!companyId) {
      return
    }

    const existing = map.get(companyId)
    if (existing) {
      map.set(companyId, {
        ...existing,
        company_name: existing.company_name || company.company_name,
        company_code: existing.company_code || company.company_code,
        company_type: existing.company_type || company.company_type
      })
      return
    }

    map.set(companyId, {
      company_id: companyId,
      company_name: company.company_name || companyId,
      company_code: company.company_code,
      company_type: company.company_type || 'company'
    })
  })

  return Array.from(map.values())
}

const prioritizeDefaultCompany = (
  companies: CompanyScopeOption[],
  defaultCompanyId: string | null
): CompanyScopeOption[] => {
  if (!defaultCompanyId) {
    return companies
  }

  const defaultIndex = companies.findIndex((company) => company.company_id === defaultCompanyId)
  if (defaultIndex <= 0) {
    return companies
  }

  const prioritized = [...companies]
  const [defaultCompany] = prioritized.splice(defaultIndex, 1)
  prioritized.unshift(defaultCompany)
  return prioritized
}

const extractCompanyFromRole = (role: RoleScopeInfo): CompanyScopeOption | null => {
  const roleCompanyId = normalizeString(role.company_id) || normalizeString(role.company?.company_id) || normalizeString(role.company?.id)
  if (!roleCompanyId) {
    return null
  }

  return {
    company_id: roleCompanyId,
    company_name:
      normalizeString(role.company_name) ||
      normalizeString(role.company?.company_name) ||
      normalizeString(role.company?.name) ||
      roleCompanyId,
    company_code:
      normalizeString(role.company_code) ||
      normalizeString(role.company?.company_code) ||
      normalizeString(role.company?.code),
    company_type: 'company'
  }
}

const extractCompaniesFromRoles = (roles: RoleScopeInfo[]): CompanyScopeOption[] => {
  return dedupeCompanies(
    roles
      .map((role) => extractCompanyFromRole(role))
      .filter((company): company is CompanyScopeOption => company !== null)
  )
}

const safeParseJson = <T>(rawValue: string | null): T | null => {
  if (!rawValue) {
    return null
  }

  try {
    return JSON.parse(rawValue) as T
  } catch (error) {
    return null
  }
}

const getSelectedCompanyFromStorage = (): CompanyScopeOption | null => {
  const rememberCompany = localStorage.getItem(REMEMBER_COMPANY_KEY)
  const primaryStorage = rememberCompany === 'true' ? localStorage : sessionStorage
  const fallbackStorage = primaryStorage === localStorage ? sessionStorage : localStorage

  const selectedRaw = primaryStorage.getItem(SELECTED_COMPANY_KEY) || fallbackStorage.getItem(SELECTED_COMPANY_KEY)
  const selected = safeParseJson<Partial<CompanyScopeOption>>(selectedRaw)

  return toCompanyOption(selected)
}

const getGlobalRolesFromPersistedAuth = (): RoleScopeInfo[] => {
  const persistedAuthRaw = localStorage.getItem(AUTH_PERSIST_KEY)
  const persistedAuth = safeParseJson<Record<string, string>>(persistedAuthRaw)
  if (!persistedAuth) {
    return []
  }

  const globalRolesRaw = persistedAuth.globalRoles
  const globalRoles = safeParseJson<RoleScopeInfo[]>(globalRolesRaw)
  return Array.isArray(globalRoles) ? globalRoles : []
}

const getSelectedCompanyRolesFromStorage = (): RoleScopeInfo[] => {
  const rememberCompany = localStorage.getItem(REMEMBER_COMPANY_KEY)
  const primaryStorage = rememberCompany === 'true' ? localStorage : sessionStorage
  const fallbackStorage = primaryStorage === localStorage ? sessionStorage : localStorage
  const selectedRaw = primaryStorage.getItem(SELECTED_COMPANY_KEY) || fallbackStorage.getItem(SELECTED_COMPANY_KEY)

  const selected = safeParseJson<{roles?: RoleScopeInfo[]}>(selectedRaw)
  const roles = selected?.roles
  return Array.isArray(roles) ? roles : []
}

export class CompanyAutocompleteScope {
  static resolveFromRoles(input: ResolveCompanyAutocompleteScopeInput): CompanyAutocompleteScopeResult {
    const roles = Array.isArray(input.roles) ? input.roles : []
    const availableCompanies = Array.isArray(input.availableCompanies)
      ? dedupeCompanies(input.availableCompanies)
      : []

    const companiesFromRoles = extractCompaniesFromRoles(roles)
    const roleCompanyIds = companiesFromRoles.map((company) => company.company_id)
    const roleCompanyIdSet = new Set(roleCompanyIds)

    const selectedCompany = toCompanyOption(input.selectedCompany)
    const defaultCompanyId = selectedCompany?.company_id || null
    const useSelectedFallback = input.includeSelectedCompanyFallback !== false

    const hasGlobalRole = roles.some((role) => roleLevelIsGlobal(role))

    if (hasGlobalRole) {
      const globalCompanyCandidates: CompanyScopeOption[] = []

      if (availableCompanies.length > 0) {
        globalCompanyCandidates.push(...availableCompanies)
      }

      if (globalCompanyCandidates.length === 0 && companiesFromRoles.length > 0) {
        globalCompanyCandidates.push(...companiesFromRoles)
      }

      if (globalCompanyCandidates.length === 0 && useSelectedFallback && selectedCompany) {
        globalCompanyCandidates.push(selectedCompany)
      }

      const companies = prioritizeDefaultCompany(dedupeCompanies(globalCompanyCandidates), defaultCompanyId)
      const defaultCompany = defaultCompanyId
        ? companies.find((company) => company.company_id === defaultCompanyId) || null
        : null

      return {
        shouldShowAutocomplete: true,
        hasGlobalRole: true,
        mode: 'global',
        companies,
        roleCompanyIds,
        defaultCompanyId,
        defaultCompany
      }
    }

    let companyScopedOptions: CompanyScopeOption[] = []

    if (availableCompanies.length > 0 && roleCompanyIdSet.size > 0) {
      companyScopedOptions = availableCompanies.filter((company) => roleCompanyIdSet.has(company.company_id))
    }

    if (companyScopedOptions.length === 0 && companiesFromRoles.length > 0) {
      companyScopedOptions = companiesFromRoles
    }

    if (companyScopedOptions.length === 0 && useSelectedFallback && selectedCompany) {
      companyScopedOptions = [selectedCompany]
    }

    const companies = prioritizeDefaultCompany(dedupeCompanies(companyScopedOptions), defaultCompanyId)
    const defaultCompany = defaultCompanyId
      ? companies.find((company) => company.company_id === defaultCompanyId) || null
      : null

    return {
      shouldShowAutocomplete: companies.length > 0,
      hasGlobalRole: false,
      mode: companies.length > 0 ? 'company' : 'hidden',
      companies,
      roleCompanyIds,
      defaultCompanyId,
      defaultCompany
    }
  }

  static resolveFromStorage(input?: Omit<ResolveCompanyAutocompleteScopeInput, 'roles' | 'selectedCompany'>): CompanyAutocompleteScopeResult {
    const selectedCompany = getSelectedCompanyFromStorage()
    const selectedCompanyRoles = getSelectedCompanyRolesFromStorage()
    const globalRoles = getGlobalRolesFromPersistedAuth()

    return CompanyAutocompleteScope.resolveFromRoles({
      roles: [...selectedCompanyRoles, ...globalRoles],
      availableCompanies: input?.availableCompanies,
      selectedCompany,
      includeSelectedCompanyFallback: input?.includeSelectedCompanyFallback
    })
  }
}

export const getCompanyAutocompleteScopeFromStorage = (
  input?: Omit<ResolveCompanyAutocompleteScopeInput, 'roles' | 'selectedCompany'>
): CompanyAutocompleteScopeResult => {
  return CompanyAutocompleteScope.resolveFromStorage(input)
}
